import {
  Button,
  CheckboxImage,
  CheckboxWrapper,
  Skeleton,
} from '@design-system';
import { useAnalyticsEmitter } from '@utils/analytics/emitter';
import classNames from 'classnames';
import { useTranslation } from 'next-i18next';
import { useCallback, useEffect, useMemo } from 'react';

import { useProductCheckoutLink } from '../../../features/checkout/hooks/useProductCheckoutLink';
import { withWorksQuery } from '../../../features/checkout/utils/worksQuery';
import { useCalculateOrderPricing } from '../../../hooks/useCalculateOrderPricing';
import { useDraws } from '../../../providers/DrawProvider';
import { CollectionSetProduct } from '../../../types/Purchasing';
import { Image } from '../../Image';
import { ProductPrice } from '../../ProductPrice/ProductPrice';
import EmbeddedContainer from '../DrawerContainer/EmbeddedContainer';

type CollectionSetFormMode = 'checkout' | 'draw' | 'selector';

const normaliseShopifyId = (id?: string | null) => id?.split('/').pop();

const ReleasesFormCompositeImage = (
  images: Array<{ url: string; alt: string }>,
  hint?: string,
) => {
  // If there are more than 9 images, rendering this composite preview defeats its
  // purpose as it will be too large compared to the thumbnails below it.
  if (images.length > 9) {
    return null;
  }

  return (
    <div className="grid aspect-square items-center rounded-sm bg-[#F3F3F3] p-3">
      {images.length === 0 && hint ? (
        <p className="text-balance text-center text-neutral-4">{hint}</p>
      ) : (
        <div className="flex flex-wrap justify-center">
          {images.map(({ alt, url }) => (
            <div
              key={alt}
              className={classNames({
                'basis-full': images.length === 1,
                'basis-1/2': images.length >= 2 && images.length <= 4,
                'basis-1/3': images.length > 4,
              })}
            >
              <Image alt={alt} src={url} width={384} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const CollectionSetForm = ({
  description,
  items,
  mode = 'selector',
  onSubmit,
  selectAll,
  toggleItem,
}: {
  description: string | null;
  items: Array<CollectionSetProduct>;
  mode?: CollectionSetFormMode;
  onSubmit?: () => void;
  selectAll: () => void;
  toggleItem: (id: string) => void;
}) => {
  const { t } = useTranslation(['product', 'authentication']);
  const { drawIdsEntered, linkedDraws, mainDraw } = useDraws();
  const checkoutLink = useProductCheckoutLink();

  const [emitter] = useAnalyticsEmitter();

  useEffect(() => {
    emitter('product selector', 'viewed');
  }, []);

  const drawIdsByProductShopifyId = useMemo(() => {
    return [mainDraw, ...linkedDraws].reduce((acc, drawWithFrames) => {
      if (!drawWithFrames) {
        return acc;
      }

      acc.set(drawWithFrames.product.shopifyId, drawWithFrames.draw.id);

      return acc;
    }, new Map<string, string>());
  }, [linkedDraws, mainDraw]);

  const getDrawIdForItem = useCallback(
    (item: CollectionSetProduct) =>
      drawIdsByProductShopifyId.get(normaliseShopifyId(item.shopifyId) ?? ''),
    [drawIdsByProductShopifyId],
  );

  const isEnteredDrawItem = useCallback(
    (item: CollectionSetProduct) => {
      const drawId = getDrawIdForItem(item);
      return (
        mode === 'draw' && Boolean(drawId && drawIdsEntered.includes(drawId))
      );
    },
    [drawIdsEntered, getDrawIdForItem, mode],
  );

  const isSelectedItem = useCallback(
    (item: CollectionSetProduct) => item.selected || isEnteredDrawItem(item),
    [isEnteredDrawItem],
  );

  const images = items.filter(isSelectedItem).map(({ image, title }) => ({
    url: image,
    alt: title,
  }));

  const calculationItems = useMemo(() => {
    if (mode === 'draw') {
      return [];
    }

    return items
      .filter(({ selected }) => selected)
      .map((item) => ({
        variantId: item.shopifyVariantId,
        quantity: item.quantity,
      }));
  }, [items, mode]);

  const { calculation: orderPriceCalculation, loading: orderPriceLoading } =
    useCalculateOrderPricing({
      items: calculationItems,
    });

  const hasNoSelectedItems = items.every((item) => !isSelectedItem(item));
  const selectedProductIds = useMemo(() => {
    return items
      .filter(isSelectedItem)
      .map(({ shopifyId }) => normaliseShopifyId(shopifyId))
      .filter((productId): productId is string => Boolean(productId));
  }, [isSelectedItem, items]);

  const selectItemsHint = hasNoSelectedItems ? (
    <p className="text-center text-neutral-4">
      {t('drawers.review.releases.selectVariantsHint')}
    </p>
  ) : null;

  const submitSection = (() => {
    if (checkoutLink) {
      return (
        <>
          {hasNoSelectedItems ? (
            <Button
              disabled
              fullWidth
              label={t('drawers.review.releases.buttonLabel')}
              size="md"
              type="button"
              variant="primary"
            />
          ) : (
            <Button
              as="a"
              fullWidth
              href={withWorksQuery(checkoutLink, selectedProductIds)}
              label={t('drawers.review.releases.buttonLabel')}
              onClick={() => {
                emitter('product selector', 'submit', {
                  'products selected list': items
                    .filter(({ selected }) => selected)
                    .map(({ title }) => title),
                  'products selected': items.filter(({ selected }) => selected)
                    .length,
                });
              }}
              size="md"
              variant="primary"
            />
          )}
          {selectItemsHint}
        </>
      );
    }

    if (mode !== 'checkout' || !onSubmit) {
      return null;
    }

    return (
      <>
        <Button
          disabled={hasNoSelectedItems}
          fullWidth
          label={t('drawers.review.releases.buttonLabel')}
          onClick={() => {
            emitter('product selector', 'submit', {
              'products selected list': items
                .filter(({ selected }) => selected)
                .map(({ title }) => title),
              'products selected': items.filter(({ selected }) => selected)
                .length,
            });
            onSubmit();
          }}
          type="submit"
          variant="primary"
        />
        {selectItemsHint}
      </>
    );
  })();

  const priceSummary = (() => {
    if (mode === 'draw') {
      return null;
    }

    if (orderPriceLoading || !orderPriceCalculation) {
      return <Skeleton className="h-5 w-full" />;
    }

    return (
      <div className={classNames('flex w-full gap-x-3')}>
        <span className="flex-1">
          {calculationItems.length > 1
            ? `${t('drawers.review.releases.setOf')} ${calculationItems.length}`
            : t('drawers.review.releases.single')}{' '}
          {orderPriceCalculation.totalDiscount > 0
            ? t('drawers.review.releases.inclDiscount')
            : null}
        </span>
        <span className={classNames('shrink-0')}>
          <ProductPrice
            inMinorUnit
            price={
              orderPriceCalculation.taxType === 'VAT'
                ? orderPriceCalculation.totalPrice
                : orderPriceCalculation.subtotalItemsPrice
            }
            skipConvertCurrency
            skipTaxCalculation
          />
        </span>
      </div>
    );
  })();

  return (
    <EmbeddedContainer>
      <div className="flex flex-col gap-6">
        <p className="hidden text-md font-bold md:block md:text-lg">
          {t('drawers.review.releases.title')}
        </p>

        {description ? <p>{description}</p> : null}

        {ReleasesFormCompositeImage(
          images,
          t('drawers.review.releases.selectVariantsHint'),
        )}

        <div className="flex flex-col gap-4">
          <div className="flex justify-between">
            <p>
              {items.length} {t('drawers.review.releases.variants')}
            </p>
            <button className="hyperlink" onClick={selectAll} type="button">
              {t('drawers.review.releases.selectAll')}
            </button>
          </div>
          <CheckboxWrapper hideLabel label="Select works">
            <div
              className={classNames(
                'grid gap-3',
                items.length === 2 ? 'grid-cols-2' : 'grid-cols-3',
              )}
            >
              {items.map((release) => {
                const enteredDrawItem = isEnteredDrawItem(release);

                return (
                  <CheckboxImage
                    key={release.shopifyId}
                    checked={release.selected || enteredDrawItem}
                    disabled={
                      enteredDrawItem || (mode !== 'draw' && orderPriceLoading)
                    }
                    id={release.shopifyId}
                    label={release.title}
                    name={release.title}
                    onChange={() => toggleItem(release.shopifyId)}
                    thumbnail={{
                      alt: release.title,
                      filename: release.image,
                    }}
                    type="checkbox"
                    value={release.shopifyId}
                  />
                );
              })}
            </div>
          </CheckboxWrapper>
        </div>

        {priceSummary}

        {submitSection}
      </div>
    </EmbeddedContainer>
  );
};

export default CollectionSetForm;
